import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Phone, Mail, Wrench, MessageSquareText, Banknote, Plus, Printer, FileText, ShieldCheck } from "lucide-react";

const STATUS_COLORS = {
  received: "bg-blue-500",
  in_progress: "bg-amber-500",
  completed: "bg-green-500",
  delivered: "bg-neutral-500",
};
const STATUS_RO = { received: "Primit", in_progress: "In lucru", completed: "Finalizat", delivered: "Predat" };

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/admin/customers/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="text-center py-12 text-neutral-500">Client inexistent. <Link to="/admin/clienti" className="text-[#00E5FF]">Inapoi</Link></div>;
  if (!data) return <div className="text-center py-12 text-neutral-500">Se incarca...</div>;

  const { customer, repairs, sms_history, stats } = data;
  const newRepairForCustomer = () => {
    navigate(`/admin/reparatii?prefill_name=${encodeURIComponent(customer.name)}&prefill_phone=${encodeURIComponent(customer.phone)}`);
  };

  return (
    <div data-testid="customer-detail-page">
      <Link to="/admin/clienti" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#00E5FF] mb-4">
        <ArrowLeft className="w-4 h-4" /> Inapoi la clienti
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">{customer.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-neutral-400">
            <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#00E5FF]" /> {customer.phone}</span>
            {customer.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#00E5FF]" /> {customer.email}</span>}
          </div>
          {customer.notes && <p className="text-sm text-neutral-300 mt-3 max-w-2xl">{customer.notes}</p>}
        </div>
        <button data-testid="customer-new-repair-btn" onClick={newRepairForCustomer} className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold px-5 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Reparatie pentru acest client
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-10">
        <StatBox icon={Wrench} label="Reparatii totale" value={stats.total_repairs} />
        <StatBox icon={Banknote} label="Cheltuit total" value={`${stats.total_spent.toFixed(0)} RON`} highlight />
        <StatBox icon={ShieldCheck} label="Garantii active" value={repairs.filter((r) => r.warranty_until && new Date(r.warranty_until) > new Date()).length} green />
        <StatBox icon={MessageSquareText} label="SMS trimise" value={stats.total_sms} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-neutral-950 border border-neutral-800 p-6">
          <h3 className="font-display text-xl uppercase mb-5">Istoric reparatii</h3>
          {repairs.length === 0 && <div className="text-sm text-neutral-500 py-6 text-center">Inca nicio reparatie pentru acest client</div>}
          <div className="space-y-2">
            {repairs.map((r) => {
              const ticket = r.id.slice(0, 8).toUpperCase();
              return (
                <div key={r.id} className="border border-neutral-800 hover:border-[#00E5FF]/50 transition-colors p-3">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <span className="font-mono text-[10px] uppercase px-2 py-1 bg-[#00E5FF]/10 text-[#00E5FF]">#{ticket}</span>
                    <span className={`text-[10px] uppercase tracking-widest text-white px-2 py-0.5 ${STATUS_COLORS[r.status] || "bg-neutral-500"}`}>{STATUS_RO[r.status] || r.status}</span>
                    <span className="font-display uppercase">{r.scooter_model}</span>
                    <span className="ml-auto text-xs text-neutral-500">{new Date(r.created_at).toLocaleDateString("ro-RO")}</span>
                  </div>
                  <p className="text-sm text-neutral-400">{r.issue_description}</p>
                  {r.warranty_months > 0 && r.warranty_until && (() => {
                    const expired = new Date(r.warranty_until) < new Date();
                    return (
                      <div data-testid={`customer-warranty-${r.id}`} className={`inline-flex items-center gap-1.5 mt-2 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${expired ? "border-red-800 text-red-400 bg-red-950/30" : "border-green-800 text-green-400 bg-green-950/30"}`}>
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Garantie {r.warranty_months} {r.warranty_months === 1 ? "luna" : "luni"} · {expired ? "EXPIRATA" : `pana la ${new Date(r.warranty_until).toLocaleDateString("ro-RO")}`}
                        {r.warranty_items && <span className="font-normal normal-case text-neutral-400">· {r.warranty_items}</span>}
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 mt-2 text-xs text-neutral-500 items-center">
                    {r.estimated_cost > 0 && <span>Est: {r.estimated_cost} RON</span>}
                    {r.final_cost != null && <span className="text-[#00E5FF] font-medium">Final: {r.final_cost} RON</span>}
                    <button onClick={() => window.open(`/admin/reparatii/${r.id}/print`, "_blank")} className="ml-auto text-neutral-400 hover:text-[#00E5FF] inline-flex items-center gap-1">
                      <Printer className="w-3.5 h-3.5" /> Printeaza
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-neutral-950 border border-neutral-800 p-6">
          <h3 className="font-display text-xl uppercase mb-5">Istoric SMS</h3>
          {sms_history.length === 0 && <div className="text-sm text-neutral-500 py-6 text-center">Inca niciun SMS</div>}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {sms_history.map((s) => (
              <div key={s.id} className="border border-neutral-800 p-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] uppercase px-1.5 py-0.5 ${s.status === "failed" ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}`}>{s.status}</span>
                  <span className="text-[10px] text-neutral-500">{new Date(s.timestamp).toLocaleString("ro-RO")}</span>
                </div>
                <p className="text-xs text-neutral-300">{s.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ icon: Icon, label, value, highlight, green }) => (
  <div className="bg-neutral-950 border border-neutral-800 p-5">
    <Icon className={`w-6 h-6 mb-3 ${highlight ? "text-[#00E5FF]" : green ? "text-green-400" : "text-neutral-400"}`} />
    <div className={`font-display text-4xl ${highlight ? "text-[#00E5FF]" : green ? "text-green-400" : "text-white"}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
  </div>
);
